import { useOnMount, useOnUnmount } from '@prophecy/utils/react/hooks';
import { isUndefined } from 'lodash-es';
import { useState } from 'react';
const styleMap = new Map();
/**
 *
 * @param href - The source URL of the style
 * @param onLoad - The callback function to be called when the style is loaded
 * @param onError - The callback function to be called when the style fails to load
 * @param removeOnUnmount - Whether to remove the style when all components that use it are unmounted
 * @param media - The media of the style
 * @param onUnmount - The callback function to be called when the style is unmounted
 * @param integrity - The integrity of the style
 * @param crossOrigin - The cross origin of the style
 * @returns
 */
export const useStyle = ({ href, onLoad, onError, removeOnUnmount = false, media = 'all', onUnmount, integrity, crossOrigin }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    useOnMount(() => {
        var _a, _b;
        let entry = styleMap.get(href);
        const handleLoad = (e) => {
            setLoaded(true);
            onLoad === null || onLoad === void 0 ? void 0 : onLoad(e);
        };
        const handleError = (e) => {
            setError(true);
            onError === null || onError === void 0 ? void 0 : onError(e);
        };
        if (entry) {
            // Increase reference count
            entry.count++;
            if (entry.status === 'loaded') {
                handleLoad(new Event('load'));
            }
            else if (entry.status === 'error') {
                handleError(new Event('error'));
            }
            else {
                // style is still loading, add listeners to the queue
                entry.loadListener = (_a = entry.loadListener) !== null && _a !== void 0 ? _a : [];
                entry.errorListener = (_b = entry.errorListener) !== null && _b !== void 0 ? _b : [];
                entry.loadListener.push(handleLoad);
                entry.errorListener.push(handleError);
            }
        }
        else {
            // Create new link element
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.media = media;
            if (integrity) {
                link.integrity = integrity;
            }
            if (!isUndefined(crossOrigin)) {
                link.crossOrigin = crossOrigin;
            }
            entry = {
                link,
                status: 'loading',
                count: 1,
                loadListener: [handleLoad],
                errorListener: [handleError]
            };
            link.onload = (e) => {
                var _a;
                entry.status = 'loaded';
                const listeners = (_a = entry.loadListener) !== null && _a !== void 0 ? _a : [];
                listeners.forEach((listener) => listener === null || listener === void 0 ? void 0 : listener(e));
                entry.loadListener = [];
                entry.errorListener = [];
            };
            link.onerror = (e) => {
                var _a;
                entry.status = 'error';
                const listeners = (_a = entry.errorListener) !== null && _a !== void 0 ? _a : [];
                listeners.forEach((listener) => listener === null || listener === void 0 ? void 0 : listener(e));
                entry.loadListener = [];
                entry.errorListener = [];
            };
            styleMap.set(href, entry);
            document.head.appendChild(link);
        }
    });
    useOnUnmount(() => {
        const entry = styleMap.get(href);
        if (!entry)
            return;
        entry.count--;
        if (removeOnUnmount && entry.count === 0) {
            entry.link.remove();
            styleMap.delete(href);
            onUnmount === null || onUnmount === void 0 ? void 0 : onUnmount();
        }
    });
    return { loaded, error };
};
